import { type FormEvent, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ConfirmModal, Loader, useToast } from '@lms/ui';
import { PERMISSIONS } from '@lms/shared-types';
import type { LmsQuestionsFile } from '@lms/shared-types';
import { usePermissions } from '@/hooks/usePermissions';
import { api, listResource } from '@/lib/api';
import { questionSuccessToast, roleActionErrorToast } from '@/lib/user-feedback';
import { formatQuestionTitle } from '@/lib/sanitize-html';
import { SafeHtml } from '@/components/SafeHtml';
import { ListFooter } from '@/components/ListPager';
import { SearchEmptyState } from '@/components/SearchEmptyState';
import { SearchInput } from '@/components/SearchInput';
import { HubBackLink } from '@/components/hub/HubBackLink';
import { useDebouncedValue } from '@/hooks/useDebouncedValue';
import { QUESTION_TYPE_OPTIONS } from './questionEditor';

interface Question {
  id: string;
  title?: string;
  stem?: string;
  type?: string;
  bankId?: string | null;
  bank?: { id: string; title?: string; name?: string };
  assessmentLinksCount?: number;
}

interface QuestionBank {
  id: string;
  title: string;
}

const QUESTION_TYPE_ICONS: Record<string, string> = {
  single_choice: 'radio_button_checked',
  multiple_choice: 'check_box',
  true_false: 'toggle_on',
  short_answer: 'short_text',
  long_answer: 'notes',
  file_upload: 'upload_file',
};

const QUESTION_TYPES = QUESTION_TYPE_OPTIONS.map((item) => ({
  ...item,
  icon: QUESTION_TYPE_ICONS[item.value] ?? 'help',
}));

function typeMeta(type?: string) {
  const found = QUESTION_TYPES.find((t) => t.value === type);
  return found ?? { value: type ?? '', label: type ?? 'Desconocido', icon: 'help' };
}

function questionTitle(row: Question) {
  const raw = row.title ?? row.stem ?? 'Sin título';
  return formatQuestionTitle(raw);
}

function readPage(params: URLSearchParams) {
  const value = Number(params.get('page'));
  return Number.isInteger(value) && value > 0 ? value : 1;
}

function questionInitial(row: Question): string {
  const raw = (row.title ?? row.stem ?? '')
    .replace(/<[^>]*>/g, '')
    .replace(/\*\*/g, '')
    .trim();
  return raw.charAt(0).toUpperCase() || '?';
}

export function PreguntasPage() {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const { hasPermission } = usePermissions();
  const canCreate = hasPermission(PERMISSIONS.PREGUNTAS_CREAR);
  const canEdit = hasPermission(PERMISSIONS.PREGUNTAS_EDITAR);
  const canDelete = hasPermission(PERMISSIONS.PREGUNTAS_ELIMINAR);
  const headerCheckboxRef = useRef<HTMLInputElement>(null);
  const [searchParams, setSearchParams] = useSearchParams();
  const [search, setSearch] = useState('');
  const [type, setType] = useState('');
  const [bankId, setBankId] = useState(() => searchParams.get('bankId') ?? '');
  const page = readPage(searchParams);
  const appliedSearch = useDebouncedValue(search, 300).trim();
  const [selectedIds, setSelectedIds] = useState<string[]>([]);
  const [pendingDeleteIds, setPendingDeleteIds] = useState<string[]>([]);
  const [exportError, setExportError] = useState<string | null>(null);
  const importedCount = searchParams.get('imported');

  function dismissImportNotice() {
    setSearchParams(
      (current) => {
        const next = new URLSearchParams(current);
        next.delete('imported');
        return next;
      },
      { replace: true },
    );
  }

  function setPage(next: number) {
    setSearchParams(
      (current) => {
        const params = new URLSearchParams(current);
        if (next <= 1) params.delete('page');
        else params.set('page', String(next));
        return params;
      },
      { replace: true },
    );
  }

  useEffect(() => {
    const bankFromUrl = searchParams.get('bankId') ?? '';
    if (bankFromUrl) setBankId(bankFromUrl);
  }, [searchParams]);

  useEffect(() => {
    if (!importedCount) return;

    const timer = window.setTimeout(dismissImportNotice, 5000);
    return () => window.clearTimeout(timer);
  }, [importedCount]);

  const { data, isLoading, error } = useQuery({
    queryKey: ['questions', bankId, appliedSearch, page],
    queryFn: () =>
      listResource<Question>('/questions', {
        page,
        ...(bankId ? { bankId } : {}),
        ...(appliedSearch ? { search: appliedSearch } : {}),
      }),
  });

  const meta = data?.meta;
  const perPage = meta?.perPage ?? 20;
  const total = meta?.total ?? 0;
  const lastPage = meta?.lastPage ?? 1;
  const rangeStart = total === 0 ? 0 : (page - 1) * perPage + 1;
  const rangeEnd = Math.min(page * perPage, total);

  const { data: banksData } = useQuery({
    queryKey: ['question-banks'],
    queryFn: () => api<{ data: QuestionBank[] }>('/questions/banks'),
  });

  const banks = banksData?.data ?? [];
  const bankTitleById = useMemo(() => new Map(banks.map((bank) => [bank.id, bank.title])), [banks]);
  const rows = data?.data ?? [];

  const filtered = useMemo(() => {
    return rows.filter((row) => !type || row.type === type);
  }, [rows, type]);

  const selectableQuestions = useMemo(
    () => filtered.filter((row) => (row.assessmentLinksCount ?? 0) === 0),
    [filtered],
  );
  const selectableIds = useMemo(() => selectableQuestions.map((row) => row.id), [selectableQuestions]);
  const allPageSelected =
    selectableIds.length > 0 && selectableIds.every((id) => selectedIds.includes(id));
  const hasPageSelection = selectableIds.some((id) => selectedIds.includes(id));

  useEffect(() => {
    setSelectedIds([]);
  }, [page, bankId, type, appliedSearch]);

  useEffect(() => {
    if (headerCheckboxRef.current) {
      headerCheckboxRef.current.indeterminate = hasPageSelection && !allPageSelected;
    }
  }, [hasPageSelection, allPageSelected]);

  const deleteMutation = useMutation({
    mutationFn: (ids: string[]) => api<{ deleted: number; skipped?: number }>('/questions/bulk-delete', {
      method: 'POST',
      body: { ids },
    }),
    onSuccess: (result) => {
      setPendingDeleteIds([]);
      setSelectedIds([]);
      toast(questionSuccessToast('delete', result.deleted));
      void queryClient.invalidateQueries({ queryKey: ['questions'] });
      void queryClient.invalidateQueries({ queryKey: ['question-banks'] });
    },
    onError: (error) => {
      toast(roleActionErrorToast(error, 'No se pudo eliminar la pregunta'));
    },
  });

  const exportMutation = useMutation({
    mutationFn: async (id: string) => {
      const file = await api<LmsQuestionsFile>(`/questions/export?bankId=${encodeURIComponent(id)}`);
      const blob = new Blob([`${JSON.stringify(file, null, 2)}\n`], { type: 'application/json' });
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = 'banco-preguntas.json';
      link.click();
      URL.revokeObjectURL(link.href);
    },
    onSuccess: () => setExportError(null),
    onError: (err) => setExportError((err as Error).message),
  });


  function toggleQuestion(id: string) {
    setSelectedIds((current) =>
      current.includes(id) ? current.filter((value) => value !== id) : [...current, id],
    );
  }

  function toggleAllOnPage() {
    if (allPageSelected) {
      setSelectedIds((current) => current.filter((id) => !selectableIds.includes(id)));
      return;
    }
    setSelectedIds((current) => [...new Set([...current, ...selectableIds])]);
  }

  function handleDelete(ids: string[]) {
    if (ids.length === 0) return;
    setPendingDeleteIds(ids);
  }

  const pendingQuestion = rows.find((row) => row.id === pendingDeleteIds[0]);
  const deleteDescription =
    pendingDeleteIds.length === 1
      ? `¿Está seguro de eliminar la pregunta «${pendingQuestion ? questionTitle(pendingQuestion) : 'esta pregunta'}»? Esta acción no se puede deshacer.`
      : `¿Está seguro de eliminar ${pendingDeleteIds.length} preguntas? Esta acción no se puede deshacer.`;

  const hasActiveFilters = Boolean(search.trim() || type || bankId);
  const bankQuery = bankId ? `?bankId=${bankId}` : '';

  const filterKey = `${appliedSearch}\0${bankId}`;
  const previousFilter = useRef(filterKey);
  useEffect(() => {
    if (previousFilter.current === filterKey) return;
    previousFilter.current = filterKey;
    setPage(1);
  }, [filterKey]);

  return (
    <div className="lms-question-bank lms-site-admin-hub-page font-sans">
      <header className="mb-6 flex items-start gap-4">
        <HubBackLink to="/administracion?tab=courses" label="Volver a Administración" />
        <div>
          <h1 className="text-3xl font-semibold text-dash-primary md:text-4xl">Bancos de Preguntas</h1>
          <p className="mt-1 max-w-2xl text-dash-on-surface-variant">
            Preguntas reutilizables en múltiples evaluaciones.
          </p>
        </div>
      </header>

      {importedCount && (
        <div
          className="mx-auto mb-4 flex max-w-5xl items-center justify-between gap-3 rounded-lg border border-green-200 bg-green-50 px-4 py-3 text-sm text-green-900"
          role="status"
        >
          <span>Se importaron {importedCount} preguntas correctamente.</span>
          <button
            type="button"
            className="text-green-800 underline-offset-2 hover:underline"
            onClick={dismissImportNotice}
          >
            Cerrar
          </button>
        </div>
      )}

      <div className="lms-question-bank__stack">
        <div className="lms-question-bank__toolbar">
          <div className="lms-question-bank__toolbar-left">
            <Link to="/preguntas/bancos" className="lms-question-bank__tool-btn lms-question-bank__tool-btn--primary">
              Bancos
            </Link>
            {canCreate && (
              <Link to={`/preguntas/importar${bankQuery}`} className="lms-question-bank__tool-btn">
                Importar JSON
              </Link>
            )}
            {bankId ? (
              <button
                type="button"
                className="lms-question-bank__tool-btn"
                onClick={() => exportMutation.mutate(bankId)}
                disabled={exportMutation.isPending}
              >
                {exportMutation.isPending ? 'Exportando…' : 'Exportar JSON'}
              </button>
            ) : null}
          </div>
          {canCreate && (
            <Link
              to={`/preguntas/create${bankQuery}`}
              className="lms-question-bank__tool-btn lms-question-bank__tool-btn--primary lms-question-bank__tool-btn--create"
            >
              <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }} aria-hidden="true">
                add_circle
              </span>
              Nueva pregunta
            </Link>
          )}
        </div>

        <form method="get" action="/preguntas" className="lms-question-bank__filters" onSubmit={(event) => event.preventDefault()}>
          <div className="lms-question-bank__filter-field">
            <label htmlFor="question-search" className="lms-question-bank__filter-label">
              Buscar
            </label>
            <SearchInput
              id="question-search"
              name="search"
              value={search}
              onChange={setSearch}
              placeholder="Buscar pregunta..."
              aria-label="Buscar pregunta"
              className="lms-question-bank__search"
            />
          </div>

          <div className="lms-question-bank__filter-field">
            <label htmlFor="question-type" className="lms-question-bank__filter-label">
              Tipo de pregunta
            </label>
            <select
              id="question-type"
              name="type"
              className="lms-question-bank__select"
              aria-label="Tipo de pregunta"
              value={type}
              onChange={(e) => setType(e.target.value)}
            >
              <option value="">Tipo de pregunta</option>
              {QUESTION_TYPES.map((t) => (
                <option key={t.value} value={t.value}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div className="lms-question-bank__filter-field">
            <label htmlFor="question-bank" className="lms-question-bank__filter-label">
              Bancos
            </label>
            <select
              id="question-bank"
              name="bank_id"
              className="lms-question-bank__select"
              aria-label="Banco"
              value={bankId}
              onChange={(e) => setBankId(e.target.value)}
            >
              <option value="">Todos los Bancos</option>
              {banks.map((bank) => (
                <option key={bank.id} value={bank.id}>
                  {bank.title}
                </option>
              ))}
            </select>
          </div>

          <div className="lms-question-bank__filter-actions">
            {hasActiveFilters && (
              <Link
                to="/preguntas"
                className="lms-question-bank__filters-clear"
                onClick={() => {
                  setSearch('');
                  setType('');
                  setBankId('');
                  setPage(1);
                }}
              >
                Quitar filtros
              </Link>
            )}
          </div>
        </form>

        {error && (
          <p className="text-sm text-red-600" role="alert">
            {(error as Error).message}
          </p>
        )}

        {exportError && (
          <p className="text-sm text-red-600" role="alert">
            {exportError}
          </p>
        )}

        {selectedIds.length > 0 && canDelete && (
          <div className="lms-question-bank__bulk">
            <p className="lms-question-bank__bulk-count">
              {selectedIds.length} {selectedIds.length === 1 ? 'pregunta seleccionada' : 'preguntas seleccionadas'}
            </p>
            <div className="lms-question-bank__bulk-form">
              <button
                type="button"
                className="lms-question-bank__bulk-delete"
                disabled={deleteMutation.isPending}
                onClick={() => handleDelete(selectedIds)}
              >
                {deleteMutation.isPending ? 'Eliminando…' : 'Eliminar seleccionadas'}
              </button>
            </div>
          </div>
        )}

        <div className="lms-gradebook lms-gradebook--forum">
          <div className="lms-course-forum-card">
            {isLoading ? (
              <div className="flex justify-center py-12">
                <Loader />
              </div>
            ) : filtered.length === 0 ? (
              <div className="lms-gradebook__table-wrap">
                <div className="lms-gradebook__empty">
                  <div className="lms-gradebook__empty-icon" aria-hidden="true">
                    <span className="material-symbols-outlined" style={{ fontVariationSettings: "'FILL' 1" }}>
                      quiz
                    </span>
                  </div>
                  {hasActiveFilters ? (
                    <SearchEmptyState
                      term={search.trim()}
                      onClear={() => {
                        setSearch('');
                        setType('');
                        setBankId('');
                        setPage(1);
                      }}
                    />
                  ) : (
                    <>
                      <h3>Sin preguntas</h3>
                      <p>No hay preguntas registradas.</p>
                    </>
                  )}
                </div>
              </div>
            ) : (
              <>
                <div className="lms-gradebook__table-wrap">
                  <table className="lms-gradebook__table">
                    <thead>
                      <tr>
                        <th className="lms-gradebook__th lms-question-bank__check" scope="col">
                          <input
                            ref={headerCheckboxRef}
                            type="checkbox"
                            className="lms-question-bank__checkbox"
                            checked={allPageSelected}
                            disabled={selectableIds.length === 0}
                            onChange={toggleAllOnPage}
                            aria-label="Seleccionar todas las preguntas de esta página"
                          />
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          ID
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Pregunta
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Tipo
                        </th>
                        <th className="lms-gradebook__th" scope="col">
                          Banco
                        </th>
                        <th className="lms-gradebook__th lms-gradebook__th--actions" scope="col">
                          Acciones
                        </th>
                      </tr>
                    </thead>
                    <tbody>
                      {filtered.map((question) => {
                        const meta = typeMeta(question.type);
                        const inUse = (question.assessmentLinksCount ?? 0) > 0;
                        const displayTitle = questionTitle(question);
                        const bankLabel =
                          question.bank?.title ??
                          question.bank?.name ??
                          (question.bankId ? bankTitleById.get(question.bankId) : undefined);
                        return (
                          <tr key={question.id}>
                            <td className="lms-gradebook__td lms-question-bank__check">
                              <input
                                type="checkbox"
                                className="lms-question-bank__checkbox"
                                checked={selectedIds.includes(question.id)}
                                disabled={inUse}
                                onChange={() => toggleQuestion(question.id)}
                                aria-label={`Seleccionar ${question.title ?? 'pregunta'}`}
                              />
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--nowrap">
                              <span className="lms-gradebook__muted">#{question.id.slice(0, 8)}</span>
                            </td>
                            <th className="lms-gradebook__td" scope="row">
                              <div className="lms-gradebook__student">
                                <div className="lms-gradebook__avatar" aria-hidden="true">
                                  {questionInitial(question)}
                                </div>
                                <div className="min-w-0">
                                  {canEdit ? (
                                    <Link to={`/preguntas/create?id=${question.id}`} className="lms-gradebook__name">
                                      <SafeHtml as="span" html={displayTitle} />
                                    </Link>
                                  ) : (
                                    <SafeHtml as="span" className="lms-gradebook__name" html={displayTitle} />
                                  )}
                                </div>
                              </div>
                            </th>
                            <td className="lms-gradebook__td">{meta.label}</td>
                            <td className="lms-gradebook__td">
                              {bankLabel ?? <span className="lms-gradebook__muted">—</span>}
                            </td>
                            <td className="lms-gradebook__td lms-gradebook__td--actions">
                              <div className="flex items-center justify-end gap-1">
                                {canEdit && (
                                  <Link
                                    to={`/preguntas/create?id=${question.id}`}
                                    className="lms-gradebook__action"
                                    title="Editar"
                                    aria-label={`Editar ${question.title ?? 'pregunta'}`}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      edit
                                    </span>
                                  </Link>
                                )}
                                {inUse ? (
                                  <button
                                    type="button"
                                    className="lms-gradebook__action"
                                    title="No se puede eliminar porque está en una evaluación"
                                    aria-label="En uso en evaluaciones"
                                    disabled
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      lock
                                    </span>
                                  </button>
                                ) : canDelete ? (
                                  <button
                                    type="button"
                                    className="lms-gradebook__action"
                                    title="Eliminar"
                                    aria-label={`Eliminar ${question.title ?? 'pregunta'}`}
                                    disabled={deleteMutation.isPending}
                                    onClick={() => handleDelete([question.id])}
                                  >
                                    <span className="material-symbols-outlined" aria-hidden="true">
                                      delete
                                    </span>
                                  </button>
                                ) : null}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                <ListFooter
                  page={page}
                  totalPages={lastPage}
                  total={total}
                  start={rangeStart}
                  end={rangeEnd}
                  onPageChange={setPage}
                />
              </>
            )}
          </div>
        </div>
      </div>

      <ConfirmModal
        open={pendingDeleteIds.length > 0}
        title={pendingDeleteIds.length === 1 ? 'Eliminar pregunta' : 'Eliminar preguntas'}
        description={deleteDescription}
        confirmLabel="Eliminar"
        loading={deleteMutation.isPending}
        onConfirm={() => deleteMutation.mutate(pendingDeleteIds)}
        onClose={() => {
          if (!deleteMutation.isPending) setPendingDeleteIds([]);
        }}
      />
    </div>
  );
}
